import { Injectable } from '@angular/core';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { AddressType, collectionFormSchema } from '../components/collection/collection-delivery.form.schema';
import { Customer, Address } from '@ct-client';
import { FormHelperService } from '@slickss/supplystack-components';
import { BackModalComponent } from '../../../_shared/components/back-modal/back-modal.component';
import { SecretService } from '../../../_core/services/secret.service';
import { Router } from '@angular/router';
import { AddressHelperService } from '../../../_core/services/address-helper.service';
import { BulletType } from '../../../_core/models/bullet-type.enum';
import { addMinutes, addDays, isAfter } from 'date-fns';
import { OOHService, OpeningHours } from '../../../_core/services/ooh.service';
import { differenceInMinutes } from 'date-fns/fp';
import {
    FormFieldOptions,
    IncoTerm,
    OrderCreationFlow,
    ServiceLevel
} from '@ct-client/src/models/interfaces/_settings.model';
import { Subject } from 'rxjs';
import { validateMatLine } from '../utils/matline.validator';
import { MatDialog } from '@angular/material/dialog';

export interface MOMergedForm {
  collection: any;
  delivery: any;
  lines: any[];
  overridePallet: number;
  overrideHangingGarment: number;
  overrideGrossWeight: number;
  overrideNetWeight: number;
  overrideGoodsValue: number;
  overrideLDM: number;
  insurance: any;
  shipper: any;
  consignee: any;
  notify: any;
}

export interface SOMergedForm {
  collections: any[];
  deliveries: any[];
  insurance: any;
  shipperConsigneeNotify: any;
}

export type FinalOrder = MOMergedForm | SOMergedForm;

@Injectable({
    providedIn: 'root'
})
export class OrderCreationService {

    private creationStepsMode = 'NORMAL_FLOW';

    private customerLength = 0;
    private customerForm: UntypedFormGroup = null;

    private collectionForm: UntypedFormGroup[] = [this.formHelperService.buildForm(collectionFormSchema)];
    private collectionAddressForm: UntypedFormGroup[] = [];
    private collectionAddressSelected: AbstractControl[] = [];
    private collectionAddressTypeahead: any[] = [];
    private collectionAddressType: AddressType[] = [];

    private deliveryForm: UntypedFormGroup[] = [this.formHelperService.buildForm(collectionFormSchema)];
    private deliveryAddressForm: UntypedFormGroup[] = [];
    private deliveryAddressSelected: AbstractControl[] = [];
    private deliveryAddressTypeahead: any[] = [];
    private deliveryAddressType: AddressType[] = [];

    private insuranceForm: UntypedFormGroup = null;
    private shipperConsigneeNotifyForm: UntypedFormGroup = null;

    private goodsList: any[] = [];
    private overridePalletCount: number[] = [];
    private overrideHangingGarmentCount: number[] = [];
    private overrideGrossWeight: number[] = [];
    private overrideNetWeight: number[] = [];
    private overrideGoodsValue: number[] = [];
    private overrideLDMValue: number[] = [];

    private documentList: any[] = null;
    private materialOrderId: any = null;

    private isRejected = false;
    private rejectionFeedback: string = null;

    public timeWithOffset = null;
    public timeWithoutOffset = null;
    public availableBulletsChange = new Subject();

    private jsOrderedDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    private orderCreationFlows: OrderCreationFlow[];
    private serviceLevels: ServiceLevel[];
    private incoTerms: IncoTerm[];

    constructor(
        public dialog: MatDialog,
        private router: Router,
        private formHelperService: FormHelperService,
        private secretService: SecretService,
        private oohService: OOHService,
        private addressHelper: AddressHelperService
    ) { }


    public getOrderLogic() {
        const settings: any = this.getSettings();
        return settings ? settings.sOrderLogic : null;
    }

    private getSettings(): any {
        return this.secretService.getPortalSettings();
    }


    public setOffsetTimings(offsett, index) {
        const isFormInvalid = !this.collectionForm[index] || !this.collectionForm[index].valid;
        const isTimeUnset = !this.timeWithOffset || !this.timeWithoutOffset;

        if (offsett && (isFormInvalid || isTimeUnset)) {
            if (this.oohService.isOOH()) {
                this.timeWithOffset = this.calculateTimeWithOffset(offsett);
            } else {
                this.timeWithOffset = addMinutes(new Date(), offsett);
            }
            this.timeWithoutOffset = new Date();
        }
    }

    public calculateTimeWithOffset(offset) {

        const officeHours = this.oohService.getOfficeHours();
        const today = new Date();

        const index = officeHours.findIndex(h => h.day.toLowerCase() === this.jsOrderedDays[today.getDay()].toLowerCase());

        if (index >= 0) {

            let nextOpenDay: OpeningHours = null;

            nextOpenDay = this.findNextOpenDay(this.willOpenOnCurrentDay(officeHours, index) ? index : (index + 1), 7, officeHours);

            if (index > 0 && !nextOpenDay) {
                nextOpenDay = this.findNextOpenDay(0, index + 1, officeHours);
            }

            const nextOpenDayIndex = this.jsOrderedDays.findIndex((v) => v.toLowerCase() === nextOpenDay.day.toLowerCase());
            const baseDate: Date = this.willOpenOnCurrentDay(officeHours, index)
                ? nextOpenDay.openingHour
                : this.findBaseDate(nextOpenDayIndex);

            const dateNoOffset = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(),
                nextOpenDay.openingHour.getHours(), nextOpenDay.openingHour.getMinutes());

            if (differenceInMinutes(new Date(), dateNoOffset) > offset) {
                return dateNoOffset;
            }

        }

        return addMinutes(new Date(), offset);

    }

    private willOpenOnCurrentDay(officeHours: OpeningHours[], index): boolean {
        const todayOfficeHours = officeHours[index];
        return isAfter(todayOfficeHours.openingHour, new Date());
    }

    private findNextOpenDay(init, range, officeHours: OpeningHours[]) {
        for (let i = init; i < range; i++) {
            const dayHours = officeHours[i];
            if (!!dayHours.openingHour || !!dayHours.closingHour) {
                return dayHours;
            }
        }
    }

    private findBaseDate(nextOpenDayIndex: number) {
        const now = new Date();
        for (let j = 1; j <= 7; j++) {
            const date: Date = addDays(now, j);
            if (date.getDay() === nextOpenDayIndex) {
                return date;
            }
        }
    }

    public unsetOffsetTimings() {
        this.timeWithOffset = null;
        this.timeWithoutOffset = null;
    }

    public getTimeWithOffset() {
        return this.timeWithOffset;
    }

    public getTimeWithoutOffset() {
        return this.timeWithoutOffset;
    }

    public setCustomerListLength(value: number) {
        this.customerLength = value;
    }

    public setCustomerForm(form: UntypedFormGroup) {
        this.customerForm = form;
    }

    public setOtherInfoForm(form: UntypedFormGroup) {
        this.insuranceForm = form;
    }

    public setShipperConsigneeNotifyForm(form: UntypedFormGroup) {
        this.shipperConsigneeNotifyForm = form;
    }

    public setGoodsList(goodsList: any[]) {
        this.goodsList = goodsList;
    }

    public setOverridePalletCount(overridePalletCount) {
        return this.overridePalletCount = overridePalletCount;
    }
    public setOverrideGrossWeight(overrideGrossWeight) {
        return this.overrideGrossWeight = overrideGrossWeight;
    }

    public setOverrideNetWeight(overrideNetWeight) {
        return this.overrideNetWeight = overrideNetWeight;
    }

    public setOverrideGoodsValue(overrideGoodsValue) {
        return this.overrideGoodsValue = overrideGoodsValue;
    }

    public setOverrideLDMValue(overrideLDMValue) {
        return this.overrideLDMValue = overrideLDMValue;
    }

    public getCustomerListLength(): number {
        return this.customerLength;
    }

    public getAllGoodsList(): any[] {
        return [].concat(...this.goodsList);
    }


    public getGoodsList(index): any[] {
        return this.goodsList ? this.goodsList[index] || [] : [];
    }

    public getOverridePalletCount(index) {
        return this.overridePalletCount[index];
    }
    public getOverrideHangingGarmentCount(index) {
        return this.overrideHangingGarmentCount[index];
    }

    public getOverrideGrossWeight(index) {
        return this.overrideGrossWeight[index];
    }

    public getOverrideNetWeight(index) {
        return this.overrideNetWeight[index];
    }

    public getOverrideGoodsValue(index) {
        return this.overrideGoodsValue[index];
    }

    public getOverrideLDMValue(index) {
        return this.overrideLDMValue[index];
    }

    public getCustomerForm(): UntypedFormGroup {
        return this.customerForm;
    }

    public getInsuranceForm(): UntypedFormGroup {
        return this.insuranceForm;
    }

    public getShipperConsigneeNotifyForm(): UntypedFormGroup {
        return this.shipperConsigneeNotifyForm;
    }

    public getCollectionForm(index: number): UntypedFormGroup {
        return this.collectionForm ? this.collectionForm[index] : null;
    }

    public getCollectionForms(): UntypedFormGroup[] {
        return this.collectionForm;
    }
    public getDeliveryForm(index: number): UntypedFormGroup {
        return this.deliveryForm ? this.deliveryForm[index] : null;
    }

    public getDeliveryForms(): UntypedFormGroup[] {
        return this.deliveryForm;
    }
    public pushEmptyCollection() {
        this.collectionForm.push(this.formHelperService.buildForm(collectionFormSchema));
    }

    public popEmptyCollection() {
        this.collectionForm.pop();
    }

    public pushEmptyDelivery() {
        this.deliveryForm.push(this.formHelperService.buildForm(collectionFormSchema));
    }

    public popEmptyDelivery() {
        this.deliveryForm.pop();
    }

    public getDocumentList() {
        return this.documentList;
    }

    public setDocumentList(documentList) {
        this.documentList = documentList;
    }

    public setIsRejected(isRejected) {
        this.isRejected = isRejected;
    }

    public setRejectionFeedback(rejectionFeedback) {
        this.rejectionFeedback = rejectionFeedback;
    }

    public isOrderRejected() {
        return this.isRejected;
    }

    public getRejectionFeedback() {
        return this.rejectionFeedback;
    }

    public getMaterialOrderID() {
        return this.materialOrderId;
    }

    public setMaterialOrderID(materialOrderId) {
        this.materialOrderId = materialOrderId;
    }

    public setCreationStepsMode(mode: string) {
        if (mode) {
            this.creationStepsMode = mode;
        }
    }

    public getCreationStepsMode() {
        return this.creationStepsMode;
    }

    public navigateBack() {
        this.openBackModal(`${this.secretService.getPortalID()}/dashboard`);
    }

    private openBackModal(value) {
        const dialogRef = this.dialog.open(BackModalComponent, {
            width: '456px',
            data: {}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // this.resetCounters();
                this.router.navigate([value]);
            }
        });
    }

    public getSOMergedForms(chosenOrderCreationFlow: OrderCreationFlow): SOMergedForm {
        const collections = [];
        const deliveries = [];

        const multipleDeliveries = chosenOrderCreationFlow === OrderCreationFlow.ONE_TO_MANY;

        this.getCollectionForms().forEach((collectionForm, index) => {
            const finalCollectionAddress = this.getFinalAddress(BulletType.COLLECTION, index);
            const collectionFormValue = this.formHelperService.cloneAbstractControl(collectionForm).value;
            this.addNumberToStreetField(finalCollectionAddress);
            this.removeHelperAddressFields(collectionFormValue);

            collections.push(
                {
                    address: finalCollectionAddress,
                    form: collectionFormValue,
                    materialLines: !multipleDeliveries ? this.getGoodsList(index) : null,
                    overrideGrossWeight: !multipleDeliveries ? this.getOverrideGrossWeight(index) : null,
                    overrideNetWeight: !multipleDeliveries ? this.getOverrideNetWeight(index) : null,
                    overrideGoodsValue: !multipleDeliveries ? this.getOverrideGoodsValue(index) : null,
                    overridePallet: !multipleDeliveries ? this.getOverridePalletCount(index) : null,
                    overrideHangingGarment: !multipleDeliveries ? this.getOverrideHangingGarmentCount(index) : null,
                    overrideLDM: !multipleDeliveries ? this.getOverrideLDMValue(index) : null
                }
            );
        });

        this.getDeliveryForms().forEach((deliveryForm, index) => {
            const finalDeliveryAddress = this.deliveryForm ? this.getFinalAddress(BulletType.DELIVERY, index) : null;
            const deliveryFormValue = this.deliveryForm ? this.formHelperService.cloneAbstractControl(deliveryForm).value : null;
            if (finalDeliveryAddress) { this.addNumberToStreetField(finalDeliveryAddress); }
            if (deliveryFormValue) { this.removeHelperAddressFields(deliveryFormValue); }

            deliveries.push(
                {
                    address: finalDeliveryAddress,
                    form: deliveryFormValue,
                    materialLines: multipleDeliveries ? this.getGoodsList(index) : null,
                    overrideGrossWeight: multipleDeliveries ? this.getOverrideGrossWeight(index) : null,
                    overrideNetWeight: multipleDeliveries ? this.getOverrideNetWeight(index) : null,
                    overrideGoodsValue: multipleDeliveries ? this.getOverrideGoodsValue(index) : null,
                    overridePallet: multipleDeliveries ? this.getOverridePalletCount(index) : null,
                    overrideHangingGarment: !multipleDeliveries ? this.getOverrideHangingGarmentCount(index) : null,
                    overrideLDM: multipleDeliveries ? this.getOverrideLDMValue(index) : null
                }
            );
        });

        return {
            collections,
            deliveries,
            insurance: this.insuranceForm.value,
            shipperConsigneeNotify: this.shipperConsigneeNotifyForm?.value
        };
    }

    public getMOMergedForms(): MOMergedForm {
        const settings = this.secretService.getPortalSettings();
        const showShipperDetails = (settings.sRequireShipperLoca ?? FormFieldOptions.HID) !== FormFieldOptions.HID;
        const showConsigneeDetails = (settings.sRequireConsigneeLoca ?? FormFieldOptions.HID) !== FormFieldOptions.HID;
        const showNotifyDetails = (settings.sRequireNotifyLoca ?? FormFieldOptions.HID) !== FormFieldOptions.HID;

        const shipperConsigneeNotifyFormValues = this.shipperConsigneeNotifyForm?.value;

        const finalCollectionAddress = this.getFinalAddress(BulletType.COLLECTION, 0);
        const finalDeliveryAddress = this.deliveryForm ? this.getFinalAddress(BulletType.DELIVERY, 0) : null;
        const finalShipperAddress = showShipperDetails && shipperConsigneeNotifyFormValues?.shipper
            ? this.getFinalShipperConsigneeAddress(shipperConsigneeNotifyFormValues.shipper)
            : null;
        const finalConsigneeAddress = showConsigneeDetails && shipperConsigneeNotifyFormValues?.consignee
            ? this.getFinalShipperConsigneeAddress(shipperConsigneeNotifyFormValues.consignee)
            : null;
        const finalNotifyAddress = showNotifyDetails && shipperConsigneeNotifyFormValues?.notify
            ? this.getFinalShipperConsigneeAddress(shipperConsigneeNotifyFormValues.notify)
            : null;

        const collectionFormValue = this.formHelperService.cloneAbstractControl(this.collectionForm[0]).value;
        const deliveryFormValue = this.deliveryForm ? this.formHelperService.cloneAbstractControl(this.deliveryForm[0]).value : null;

        this.addNumberToStreetField(finalCollectionAddress);
        if (finalDeliveryAddress) { this.addNumberToStreetField(finalDeliveryAddress); }

        this.removeHelperAddressFields(collectionFormValue);
        if (deliveryFormValue) { this.removeHelperAddressFields(deliveryFormValue); }

        return {
            collection: { ...collectionFormValue, address: finalCollectionAddress },
            delivery: this.deliveryForm ? { ...deliveryFormValue, address: finalDeliveryAddress } : null,
            lines: this.goodsList,
            overridePallet: this.getOverridePalletCount(0),
            overrideHangingGarment: this.getOverrideHangingGarmentCount(0),
            overrideGrossWeight: this.getOverrideGrossWeight(0),
            overrideNetWeight: this.getOverrideNetWeight(0),
            overrideGoodsValue: this.getOverrideGoodsValue(0),
            overrideLDM: this.getOverrideLDMValue(0),
            insurance: this.insuranceForm.value,
            shipper: finalShipperAddress && shipperConsigneeNotifyFormValues?.shipper
                ? {
                    ...shipperConsigneeNotifyFormValues?.shipper?.value,
                    address: finalShipperAddress,
                    contact: shipperConsigneeNotifyFormValues?.shipper?.contact
                }
                : null,
            consignee: finalConsigneeAddress && shipperConsigneeNotifyFormValues?.consignee
                ? {
                    ...shipperConsigneeNotifyFormValues?.consignee?.value,
                    address: finalConsigneeAddress,
                    contact: shipperConsigneeNotifyFormValues?.consignee?.contact
                }
                : null,
            notify: finalNotifyAddress && shipperConsigneeNotifyFormValues?.notify
                ? {
                    ...shipperConsigneeNotifyFormValues?.notify?.value,
                    address: finalNotifyAddress,
                    contact: shipperConsigneeNotifyFormValues?.notify?.contact
                }
                : null,
        };
    }

    private addNumberToStreetField(address) {
        if (!!address && !!address.nNumber) {
            address.sAddr = `${address.sAddr} ${address.nNumber}`;
        }
    }

    private removeHelperAddressFields(formValue) {
        delete formValue.filterAddress;
        delete formValue.quickAddress;
        delete formValue.selectedAddress;
    }

    private getFinalAddress(bulletType: BulletType, index: number): Address {
        const stepForms = this.getSavedFormStep(bulletType, index);

        const selectedAddress = (!!stepForms.addressSelected && !!stepForms.addressSelected.value) ? stepForms.addressSelected.value : null;
        const addressToSet = stepForms.addressTypeahead ? stepForms.addressTypeahead : null;
        const addressFormToSet = stepForms.addressForm;

        if (addressFormToSet) {
            return this.parseFormToAddressDTO(addressFormToSet.value);
        } else if (addressToSet) {
            return addressToSet;
        } else if (selectedAddress) {
            return selectedAddress;
        }

        return null;
    }

    public isFormStepFilled(type: BulletType, index: number) {
        switch (type) {
            case BulletType.COLLECTION:
                return this.getCollectionForm(index) ? this.getCollectionForm(index).valid : false;
            case BulletType.DELIVERY:
                return this.getDeliveryForm(index) ? this.getDeliveryForm(index).valid : false;
            case BulletType.GOODS:
                return (!!this.getGoodsList(index) && this.getGoodsList(index).length);
            default:
                return null;
        }
    }

    public setCollectionDeliveryForms(form: UntypedFormGroup, address: UntypedFormGroup, selectedAddress: AbstractControl,
                                      typeaheadAddress: any, addressType: AddressType, type: BulletType, index: number) {
        const id = index;

        switch (type) {
            case BulletType.COLLECTION:
                if (this.collectionForm.find(f => f.get('id').value === id)) {
                    this.collectionForm[id] = form;
                    this.collectionAddressForm[id] = address;
                    this.collectionAddressSelected[id] = selectedAddress;
                    this.collectionAddressTypeahead[id] = typeaheadAddress;
                    this.collectionAddressType[id] = addressType;
                } else {
                    if (this.collectionForm[this.collectionForm.length - 1].get('id').value === null) {
                        this.collectionForm.pop();
                    }
                    this.collectionForm.push(form);
                    this.collectionAddressForm.push(address);
                    this.collectionAddressSelected.push(selectedAddress);
                    this.collectionAddressTypeahead.push(typeaheadAddress);
                    this.collectionAddressType.push(addressType);
                }
                break;
            case BulletType.DELIVERY:
                if (this.deliveryForm.find(f => f.get('id').value === id)) {
                    this.deliveryForm[id] = form;
                    this.deliveryAddressForm[id] = address;
                    this.deliveryAddressSelected[id] = selectedAddress;
                    this.deliveryAddressTypeahead[id] = typeaheadAddress;
                    this.deliveryAddressType[id] = addressType;
                } else {
                    if (this.deliveryForm[this.deliveryForm.length - 1].get('id').value === null) {
                        this.deliveryForm.pop();
                    }
                    this.deliveryForm.push(form);
                    this.deliveryAddressForm.push(address);
                    this.deliveryAddressSelected.push(selectedAddress);
                    this.deliveryAddressTypeahead.push(typeaheadAddress);
                    this.deliveryAddressType.push(addressType);
                }
                break;
            default:
                break;
        }
    }

    public setGoods(
        id: number,
        goodsList,
        overrideTotalPallet,
        overrideTotalHangingGarment,
        overrideTotalGrossWeight,
        overrideTotalNetWeight,
        overrideTotalgoodsValue,
        overrideLDMValue
    ) {
        this.goodsList[id] = goodsList;
        this.overridePalletCount[id] = overrideTotalPallet;
        this.overrideHangingGarmentCount[id] = overrideTotalHangingGarment;
        this.overrideGrossWeight[id] = overrideTotalGrossWeight;
        this.overrideNetWeight[id] = overrideTotalNetWeight;
        this.overrideGoodsValue[id] = overrideTotalgoodsValue;
        this.overrideLDMValue[id] = overrideLDMValue;
    }

    public getSavedFormStep(type: BulletType, index: number) {
        const cloneControl = this.formHelperService.cloneAbstractControl.bind(this.formHelperService);

        const getFormStep = (form, addressForm, addressSelected, addressTypeahead, addressType) => ({
            form: form[index] ? cloneControl(form[index]) as UntypedFormGroup : null,
            addressForm: addressForm[index] ? cloneControl(addressForm[index]) as UntypedFormGroup : null,
            addressSelected: addressSelected[index] ? cloneControl(addressSelected[index]) : null,
            addressTypeahead: addressTypeahead[index] || null,
            addressType: addressType[index] || null
        });

        switch (type) {
            case BulletType.COLLECTION:
                return getFormStep(this.collectionForm, this.collectionAddressForm, this.collectionAddressSelected,
                                   this.collectionAddressTypeahead, this.collectionAddressType);
            case BulletType.DELIVERY:
                return getFormStep(this.deliveryForm, this.deliveryAddressForm, this.deliveryAddressSelected,
                                   this.deliveryAddressTypeahead, this.deliveryAddressType);
            default:
                return null;
        }
    }

    public parseFormToAddressDTO(address): Address {
        return Object.assign({
            sAddr: (address.street) ? `${address.street}${(address.number) ? ` ${address.number}` : ''}` : null,
            sUnit: (address.unit) ? address.unit : null,
            // nNumber: (!!address.number) ? address.number : null,
            sPost: (address.postalCode) ? address.postalCode : null,
            sCity: (address.city) ? address.city : null,
            sCtry: (address.country) ? address.country.code : null
        });
    }

    public areSameAddresses(address, index: number): boolean {
        const stepForms = this.getSavedFormStep(BulletType.COLLECTION, index);

        const selectedAddress = (!!stepForms.addressSelected && !!stepForms.addressSelected.value) ? stepForms.addressSelected.value : null;
        const addressToSet = stepForms.addressTypeahead;
        const addressFormToSet = stepForms.addressForm;

        if (addressFormToSet) {
            const collection = this.parseFormToAddressDTO(addressFormToSet.value);
            return this.compareAddresses(collection, address);
        } else if (addressToSet) {
            return this.compareAddresses(addressToSet, address);
        } else if (selectedAddress) {
            return this.compareAddresses(selectedAddress, address);
        }

        return false;
    }

    private compareAddresses(collectionAddress, deliveryAddress): boolean {
        return (this.getAddressString(collectionAddress).toLowerCase() === this.getAddressString(deliveryAddress).toLowerCase());
    }

    private getAddressString(address: Address): string {
        return this.addressHelper.getAddressString(address);
    }

    private getAddressName(address: Address): string {
        return address.sLocaName;
    }

    public getCurrentCustomer(): Customer {
        return this.customerForm ? this.customerForm.get('customer').value : null;
    }

    public getCollectionAddressString(index: number): string {
        return this.getFinalAddress(BulletType.COLLECTION, index)
            ? this.getAddressString(this.getFinalAddress(BulletType.COLLECTION, index))
            : null;
    }

    public getCollectionAddressNameString(index: number): string {
        const addressName = this.getFinalAddress(BulletType.COLLECTION, index)
            ? this.getAddressName(this.getFinalAddress(BulletType.COLLECTION, index)) : null;
        return addressName ? addressName
            : !!this.collectionForm[index] && !!this.collectionForm[index].value ? this.collectionForm[index].value.companyName : null;
    }

    public getDeliveryAddressNameString(index: number): string {
        const finalAddress = this.getFinalAddress(BulletType.DELIVERY, index);
        if (finalAddress) {
            return this.getAddressName(finalAddress);
        }

        const deliveryForm = this.deliveryForm[index];
        if (deliveryForm && deliveryForm.value) {
            return deliveryForm.value.companyName;
        }

        return null;
    }

    public getDeliveryAddressString(index: number): string {
        const finalAddress = this.getFinalAddress(BulletType.DELIVERY, index);
        if (finalAddress) {
            return this.getAddressString(finalAddress);
        }
        return null;
    }

    public getTotalGoods(goodsList) {

        const total = {
            goodsValue: 0,
            quantity: 0,
            grossWeight: 0,
            netWeight: 0,
            volume: 0
        };

        if (goodsList) {
            goodsList.forEach(good => {
                total.quantity += good.quantity;
                total.volume += (good.quantity * (good.height * good.length * good.width));
                total.grossWeight += (good.quantity * good.grossWeight);
                total.netWeight += (good.quantity * good.netWeight);
                total.goodsValue += (good.quantity * good.goodsValue);
            });
        }

        (total.volume as any) = (total.volume).toFixed(2);
        (total.grossWeight as any) = (total.grossWeight).toFixed(2);
        (total.netWeight as any) = (total.netWeight).toFixed(2);
        (total.goodsValue as any) = (total.goodsValue).toFixed(2);


        return total;

    }

    public checkResetForms(router: Router) {
        if (router.routerState.snapshot.url.indexOf('/order/create') < 0) {
            this.resetForms();
        }
    }

    public setOrderCreationFlows(flows: OrderCreationFlow[]) {
        this.orderCreationFlows = flows;
    }

    public getOrderCreationFlows(): OrderCreationFlow[] {
        return this.orderCreationFlows;
    }

    public setServiceLevels(serviceLevels: ServiceLevel[]) {
        this.serviceLevels = serviceLevels;
    }

    public getServiceLevels(): ServiceLevel[] {
        return this.serviceLevels;
    }

    public setIncoTerms(incoTerms: IncoTerm[]) {
        this.incoTerms = incoTerms;
    }

    public getIncoTerms(): IncoTerm[] {
        return this.incoTerms;
    }

    public resetForms() {
        this.customerLength = 0;
        this.customerForm = null;

        this.collectionForm = [this.formHelperService.buildForm(collectionFormSchema)];
        this.collectionAddressForm = [];
        this.collectionAddressSelected = [];
        this.collectionAddressTypeahead = [];
        this.collectionAddressType = [];

        this.deliveryForm = [this.formHelperService.buildForm(collectionFormSchema)];
        this.deliveryAddressForm = [];
        this.deliveryAddressSelected = [];
        this.deliveryAddressTypeahead = [];
        this.deliveryAddressType = [];

        this.insuranceForm = null;
        this.shipperConsigneeNotifyForm = null;

        this.goodsList = [];

        this.overridePalletCount = [];
        this.overrideHangingGarmentCount = [];
        this.overrideGrossWeight = [];
        this.overrideNetWeight = [];
        this.overrideGoodsValue = [];
        this.overrideLDMValue = [];
        this.documentList = [];
        this.materialOrderId = null;

        this.isRejected = false;
        this.rejectionFeedback = null;
    }

    public validate(order) {
        const setting = this.secretService.getPortalSettings();
        let error = null;

        if (!setting) {
            console.error('no settings?');
            return null;
        }

        if (order.matOrder) {
            const linesHaveError = order.matOrder.materialLines.some((line) => {
                const e = validateMatLine(line, setting);
                error = e;
                return !!e;
            });

            if (linesHaveError) {
                return error;
            }
        }
        if (order.salesOrder) {
            const linesHaveError = order.salesOrder.matOrders.some((matOrder) => {
                return matOrder.materialLines.some((line) => {
                    const e = validateMatLine(line, setting);
                    error = e;
                    return !!e;
                });
            });

            if (linesHaveError) {
                return error;
            }
        }
        return null;
    }

    getFinalShipperConsigneeAddress(shipperConsigneeNotifyForm: any){
        let finalAddress = null;

        if (shipperConsigneeNotifyForm?.quickAddress) {
            finalAddress = shipperConsigneeNotifyForm.quickAddress;
            finalAddress.sLocaName = shipperConsigneeNotifyForm.companyName;
            this.addNumberToStreetField(finalAddress);
        }
        else if (shipperConsigneeNotifyForm?.selectedAddress) {
            finalAddress = shipperConsigneeNotifyForm.selectedAddress;
        }
        else if (shipperConsigneeNotifyForm?.address) {
            finalAddress = this.parseFormToAddressDTO(shipperConsigneeNotifyForm.address);
            finalAddress.sLocaName = shipperConsigneeNotifyForm.companyName;
        }

        return finalAddress;
    }

}

